import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useReferralOverlay } from "../../context/ReferralOverlayContext";

import {
  CREATOR_SPEND_SECTIONS,
  FAN_SPEND_SECTIONS,
  STARTER_PACK,
  SPEND,
  loyaltyBonusFromBalance,
  daysUntilMonthEnd,
  creditCapForRole,
} from "../../constants/creditsConfig";
import { formatCount } from "../../utils/formatCount";
import {
  fetchCreditSummary,
  fetchMonthlyEarningsBreakdown,
  fetchCreatorDropMilestoneProgress,
  nominateCreator,
  createStarterPackCheckout,
  nominationCost,
  wallNoteCost,
} from "../../services/creditsService";
import { dispatchPipEvent } from "../../services/pipService";
import { fetchUserByHandle } from "../../services/userLookupService";
import WallNoteModal from "../credits/WallNoteModal";
import PipAnchor from "../Pip/PipAnchor";
import "./PerksCreditsHub.css";

const THEMES_SPEND_SECTION = CREATOR_SPEND_SECTIONS.find((s) => s.id === "themes");

export function ThemeCatalogSpendCard() {
  const item = THEMES_SPEND_SECTION?.items?.[0];
  if (!THEMES_SPEND_SECTION || !item?.href) return null;
  return (
    <div className="pch-section">
      <h3 className="pch-section-title">{THEMES_SPEND_SECTION.title}</h3>
      <div className="pch-items">
        <Link to={item.href} className="pch-item pch-item--action">
          <div className="pch-item-top">
            <span className="pch-item-name">{item.name}</span>
            <span className="pch-item-cost">
              {typeof item.cost === "number" ? `${item.cost} cr` : item.cost}
            </span>
          </div>
          <p className="pch-item-desc">{item.desc}</p>
          <p className="pch-item-desc pch-item-hint">Open catalog →</p>
        </Link>
      </div>
    </div>
  );
}

export function ReferralSpendCard() {
  const { openReferral } = useReferralOverlay();
  return (
    <div className="pch-section">
      <h3 className="pch-section-title">Invite friends</h3>
      <div className="pch-items">
        <button type="button" className="pch-item pch-item--action" onClick={openReferral}>
          <div className="pch-item-top">
            <span className="pch-item-name">Referral tools</span>
            <span className="pch-item-cost">+50 cr</span>
          </div>
          <p className="pch-item-desc">
            Share your link. You both earn 50 credits after their first drop unlock.
          </p>
          <p className="pch-item-desc pch-item-hint">Open referral tools →</p>
        </button>
      </div>
    </div>
  );
}

export default function PerksCreditsHub({
  userId,
  isCreator = false,
  balance = null,
  onRefresh,
}) {
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [nominateHandle, setNominateHandle] = useState("");
  const [nominateTarget, setNominateTarget] = useState(null);
  const [status, setStatus] = useState("");
  const [starterLoading, setStarterLoading] = useState(false);
  const [wallNoteOpen, setWallNoteOpen] = useState(false);
  const [wallNoteCreator, setWallNoteCreator] = useState(null);
  const [wallHandle, setWallHandle] = useState("");

  useEffect(() => {
    if (!userId) return;
    fetchCreditSummary(userId, isCreator).then((s) => {
      setSummary(s);
      onRefresh?.();
    });
    if (isCreator) {
      fetchMonthlyEarningsBreakdown(userId).then(setBreakdown);
      fetchCreatorDropMilestoneProgress(userId).then(setMilestone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per user/role
  }, [userId, isCreator]);

  const sections = isCreator
    ? [...CREATOR_SPEND_SECTIONS, ...FAN_SPEND_SECTIONS.filter((s) => s.id !== "support")]
    : FAN_SPEND_SECTIONS;

  const nomCost = nominationCost(isCreator);
  const noteCost = wallNoteCost(isCreator);

  const handleNominateLookup = async () => {
    const handle = nominateHandle.trim().replace(/^@+/, "");
    if (!handle) return;
    setStatus("");
    const user = await fetchUserByHandle(handle);
    if (!user) {
      setStatus("Creator not found — check the @handle.");
      setNominateTarget(null);
      return;
    }
    setNominateTarget(user);
  };

  const handleNominate = async () => {
    if (!nominateTarget?.uid) return;
    setStatus("");
    try {
      await nominateCreator(nominateTarget.uid);
      dispatchPipEvent("nominate_creator");
      setStatus(`Nominated @${nominateTarget.handle || nominateTarget.username}! ${nomCost} credits spent.`);
      setNominateHandle("");
      setNominateTarget(null);
      onRefresh?.();
      fetchCreditSummary(userId, isCreator).then(setSummary);
    } catch (err) {
      setStatus(err.message || "Nomination failed.");
    }
  };

  const handleStarterPack = async () => {
    setStarterLoading(true);
    setStatus("");
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const { url } = await createStarterPackCheckout({
        successUrl: `${origin}/perks?starter=success`,
        cancelUrl: `${origin}/perks?starter=cancelled`,
      });
      if (url) window.location.href = url;
    } catch (err) {
      setStatus(err.message || "Checkout failed.");
    } finally {
      setStarterLoading(false);
    }
  };

  const openWallNoteForHandle = async () => {
    const handle = wallHandle.trim().replace(/^@+/, "");
    if (!handle) return;
    setStatus("");
    const user = await fetchUserByHandle(handle);
    if (!user) {
      setStatus("Creator not found — check the @handle.");
      return;
    }
    setWallNoteCreator(user);
    setWallNoteOpen(true);
  };

  const handleSpendAction = (itemId) => {
    if (itemId === "wall_note") {
      if (wallNoteCreator) {
        setWallNoteOpen(true);
      } else {
        setStatus("Enter a creator @handle below, then tap Send Wall Note.");
      }
    }
  };

  if (!summary) {
    return <div className="pch-loading">Loading credits…</div>;
  }

  const cap = creditCapForRole(isCreator);
  const displayBalance = balance != null ? balance : summary.balance;
  const loyalty = loyaltyBonusFromBalance(displayBalance);
  const daysLeft = daysUntilMonthEnd();

  return (
    <div className="pch">
      <div className="pch-balance-card">
        <span className="pch-eyebrow">Your balance</span>
        <div className="pch-balance-row">
          <PipAnchor id="perks-balance">
            <span className="pch-balance">{formatCount(displayBalance)}</span>
          </PipAnchor>
          <span className="pch-balance-label">credits</span>
        </div>
        <div className="pch-meta">
          <span>Cap: {formatCount(cap)}</span>
          <span>·</span>
          <span>{daysLeft} days until reset</span>
          <span>·</span>
          <span>{summary.currentStreak} day streak</span>
        </div>
        <p className="pch-loyalty">
          If you don&apos;t spend your credits you&apos;ll carry over{" "}
          <strong>{formatCount(loyalty)} credits</strong> on the 1st (10% loyalty bonus, min 50).
        </p>
      </div>

      {!summary.starterPackPurchased && (
        <div className="pch-starter">
          <div>
            <div className="pch-starter-title">Jump in faster</div>
            <div className="pch-starter-desc">
              {STARTER_PACK.credits} credits · ${STARTER_PACK.priceCad} CAD · One time only
            </div>
          </div>
          <button
            type="button"
            className="pch-starter-btn"
            onClick={handleStarterPack}
            disabled={starterLoading}
          >
            {starterLoading ? "Loading…" : "Buy starter pack"}
          </button>
        </div>
      )}

      {isCreator && breakdown && (
        <div className="pch-earnings">
          <h3 className="pch-section-title">Credits earned this month</h3>
          <div className="pch-earn-grid">
            <div className="pch-earn-item"><strong>{breakdown.checkIns}</strong><span>Check-ins</span></div>
            <div className="pch-earn-item"><strong>{breakdown.drops100}</strong><span>Drops at 100 views</span></div>
            <div className="pch-earn-item"><strong>{breakdown.nominations}</strong><span>Nominations received</span></div>
            <div className="pch-earn-item"><strong>{breakdown.wallNotes}</strong><span>Wall notes received</span></div>
            <div className="pch-earn-item"><strong>{breakdown.tips}</strong><span>Tip bonuses</span></div>
          </div>
          {milestone?.remaining > 0 && (
            <p className="pch-milestone">
              {milestone.remaining} drop{milestone.remaining === 1 ? "" : "s"} until your next milestone bonus
            </p>
          )}
        </div>
      )}

      <div className="pch-nominate-quick">
        <h3 className="pch-section-title">Nominate a creator</h3>
        <p className="pch-section-sub">{nomCost} credits · max 1 per creator per day</p>
        <div className="pch-nominate-row">
          <input
            type="text"
            placeholder="@handle"
            value={nominateHandle}
            onChange={(e) => {
              setNominateHandle(e.target.value);
              setNominateTarget(null);
            }}
            onBlur={handleNominateLookup}
          />
          <button type="button" onClick={nominateTarget ? handleNominate : handleNominateLookup}>
            {nominateTarget ? "Nominate" : "Look up"}
          </button>
        </div>
        {nominateTarget && (
          <p className="pch-nominate-found">
            Found: {nominateTarget.displayName} (@{nominateTarget.handle || nominateTarget.username})
          </p>
        )}
        <Link to="/featured/community" className="pch-link">See nomination leaderboard →</Link>
      </div>

      <div className="pch-nominate-quick">
        <h3 className="pch-section-title">Send appreciation wall note</h3>
        <p className="pch-section-sub">{noteCost} credits · 250–500 characters · max 1 per creator per month</p>
        <div className="pch-nominate-row">
          <input
            type="text"
            placeholder="@handle"
            value={wallHandle}
            onChange={(e) => setWallHandle(e.target.value)}
          />
          <button type="button" onClick={openWallNoteForHandle}>Compose</button>
        </div>
      </div>

      {status && <p className="pch-status">{status}</p>}

      {sections.map((section) => (
        section.id === "themes" ? (
          <ThemeCatalogSpendCard key={section.id} />
        ) : (
        <div key={section.id} className="pch-section">
          <h3 className="pch-section-title">{section.title}</h3>
          <div className="pch-items">
            {section.items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`pch-item${item.id === "wall_note" ? " pch-item--action" : ""}`}
                onClick={() => item.id === "wall_note" && handleSpendAction(item.id)}
                disabled={item.id !== "wall_note"}
              >
                <div className="pch-item-top">
                  <span className="pch-item-name">{item.name}</span>
                  <span className="pch-item-cost">
                    {typeof item.cost === "number" ? `${item.cost} cr` : item.cost}
                  </span>
                </div>
                <p className="pch-item-desc">{item.desc}</p>
                {item.id === "free_unlock" && (
                  <p className="pch-item-desc pch-item-hint">
                    {summary.freeUnlocksRemaining} of {SPEND.freeUnlock.monthlyCap} credit unlocks left this month
                  </p>
                )}
                {item.id === "nomination" && (
                  <p className="pch-item-desc pch-item-hint">Use the form above or nominate from a creator profile</p>
                )}
              </button>
            ))}
          </div>
        </div>
        )
      ))}

      {wallNoteCreator && (
        <WallNoteModal
          open={wallNoteOpen}
          onClose={() => setWallNoteOpen(false)}
          creatorId={wallNoteCreator.uid}
          creatorName={wallNoteCreator.displayName || wallNoteCreator.handle}
          onSuccess={() => {
            dispatchPipEvent("wall_note_sent");
            onRefresh?.();
            fetchCreditSummary(userId, isCreator).then(setSummary);
          }}
        />
      )}
    </div>
  );
}
